import React, { useState } from 'react'
import { Button } from '@arnsbogroup/react-voicearchive'

import TextInput from '../filters/TextInput'
import DateInput from '../filters/DateInput'
import NumberInput from '../filters/NumberInput'
import ApiSelect from '../filters/ApiSelect'
import DataTableOutput from '../outputs/DataTableOutput'
import StatOutput from '../outputs/StatOutput'
import BarChartOutput from '../outputs/BarChartOutput'

const FilterComponents = { TextInput, DateInput, NumberInput, ApiSelect }
const OutputComponents  = { DataTable: DataTableOutput, Stat: StatOutput, BarChart: BarChartOutput }

const toUnixTimestamp = dateString =>
  dateString ? Math.floor(new Date(dateString).getTime() / 1000) : ''

const buildQueryString = (report, filterState) => {
  const params = new URLSearchParams()
  report.filters.forEach(({ key, component }) => {
    const value = filterState[key]
    if (value == null || value === '') {
      return
    }
    params.set(key, component === 'DateInput' ? toUnixTimestamp(value) : (value?.value ?? value))
  })
  return params.toString()
}

const ReportRenderer = ({ report }) => {
  const [filterState, setFilterState] = useState(() =>
    report.filters.reduce((acc, f) => ({ ...acc, [f.key]: null }), {})
  )
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  const onFilterChange = (key, value) =>
    setFilterState(prev => ({ ...prev, [key]: value }))

  const onApply = () => {
    setLoading(true)
    setError(null)
    const qs = buildQueryString(report, filterState)
    fetch(`/rest/v2/dynamicreports/${report.id}?${qs}`, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(({ data: responseData, errors }) => {
        if (errors?.length) {
          setError(errors[0])
        } else {
          setData(responseData)
        }
        setLoading(false)
      })
      .catch(() => { setError('Request failed'); setLoading(false) })
  }

  const statOutputs    = report.outputs.filter(o => o.component === 'Stat')
  const nonStatOutputs = report.outputs.filter(o => o.component !== 'Stat')

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        {report.filters.map(filter => {
          const FilterComponent = FilterComponents[filter.component]
          if (!FilterComponent) return null
          return (
            <div key={filter.key} className="min-w-[200px]">
              <FilterComponent
                descriptor={filter}
                value={filterState[filter.key]}
                onChange={onFilterChange}
              />
            </div>
          )
        })}
        <Button onClick={onApply} disabled={loading}>
          {loading ? 'Loading...' : 'Apply Filters'}
        </Button>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      {data && !loading && (
        <div className="space-y-6">
          {statOutputs.length > 0 && (
            <dl className="grid grid-cols-3 gap-4">
              {statOutputs.map(output => (
                <StatOutput
                  key={`${output.key}-${output.label}`}
                  descriptor={output}
                  data={data[output.key]}
                />
              ))}
            </dl>
          )}
          {nonStatOutputs.map(output => {
            const OutputComponent = OutputComponents[output.component]
            if (!OutputComponent) return null
            return (
              <OutputComponent
                key={`${output.key}-${output.label}`}
                descriptor={output}
                data={data[output.key]}
                pageSize={report.pageSize ?? 50}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ReportRenderer
